import { ReactComponent as DownIcon } from 'assets/icon/chevron-down.svg';
import { ReactComponent as CrossIcon } from 'assets/icon/x.svg';
import { useState } from 'react';
import { IDefaultOption } from '../select';
import { useTranslation } from 'react-i18next';

interface ISelectProps<T> {
  selected: T[];
  setSelected: (data: T[]) => void;
  placeholder: string;
  options: T[];
  isSearchable?: boolean;
  search?: string;
  setSearch?: (data: string) => void;
  removeSearchOnBlur?: boolean;
  error?: string;
  dropdownPlacement?: 'TOP' | 'BOTTOM';
  disabled?: boolean;
  height?: string;
}

const MultiSelect = <T extends IDefaultOption>({
  selected = [],
  setSelected = () => {},
  placeholder = '',
  options = [],
  disabled = false,
  isSearchable = false,
  search = '',
  setSearch = data => {},
  removeSearchOnBlur = false,
  error = '',
  height = 'h-10',
}: ISelectProps<T>) => {
  const [show, setShow] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);

  const { t } = useTranslation();
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShow(false);
      setEdit(false);
      if (removeSearchOnBlur) {
        setSearch('');
      }
    }
  };
  const onClick = (edit: boolean, show: boolean) => {
    setEdit(edit);
    setShow(show);
  };
  const onselect = (option: T) => {
    if (!selected?.find(({ name }) => name === option.name)) {
      setSelected([...selected, option]);
    }
    setShow(false);
    setEdit(false);
  };
  const handleRemoveOption = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, option: T) => {
    const updatedValue = selected?.filter(item => item?.name !== option?.name);
    setSelected(updatedValue);
    e.stopPropagation();
  };

  return (
    <div
      className="flex items-center relative w-full bg-white h-fit z-100"
      onBlur={onBlur}
      tabIndex={0}
    >
      <div
        id={'select-box'}
        className={`flex items-center outline-none relative rounded-1 border-1.5 w-full ${height} hover:border-active cursor-pointer px-4 py-4 ${
          show ? 'border-active' : 'border-grey'
        } ${error ? 'border-red-600' : ''} ${disabled ? ' bg-grey1 opacity-50' : 'bg-white'}`}
        onClick={() => (!disabled ? (isSearchable ? onClick(true, true) : setShow(!show)) : {})}
      >
        <div className="flex flex-1 items-center w-[calc(100%-24px)] overflow-hidden">
          {isSearchable && edit ? (
            <div className="flex items-center" onClick={() => setShow(true)}>
              <input
                className="w-full border-0 text-base font-medium outline-none text-black2"
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          ) : selected.length ? (
            <div className="flex items-center w-full gap-x-1 overflow-x-auto no-scrollbar">
              {selected.map(item => (
                <div
                  className="flex items-center rounded-1 px-2 py-1 bg-grey1 gap-x-2"
                  key={item?.id}
                >
                  <label className="text-base font-medium text-grey3 whitespace-nowrap">
                    {item?.name}
                  </label>
                  <CrossIcon
                    className="cursor-pointer text-grey3"
                    height={16}
                    width={16}
                    onClick={e => handleRemoveOption(e, item)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <label className="text-sm font-regular text-grey2 whitespace-nowrap overflow-hidden text-ellipsis w-full">
              {placeholder}
            </label>
          )}
        </div>
        <DownIcon
          height={20}
          width={20}
          className={`flex items-center justify-center ${show ? 'rotate-180' : ''}`}
        />
      </div>
      <div
        className={`flex flex-col absolute top-[52px] w-full max-h-52 rounded-3 box-border z-10 bg-white shadow-md ${
          show ? 'flex' : 'hidden'
        }`}
      >
        {options?.length ? (
          <div className="flex flex-col w-full py-2 px-2 h-full overflow-y-auto">
            {options?.map(option => (
              <div
                className="flex items-center rounded-1 px-2 hover:bg-primary hover:text-white text-grey2 py-1"
                key={option?.id}
                onClick={() => onselect(option)}
              >
                <label className="text-base whitespace-nowrap">{option?.name}</label>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full py-2">
            <label className="font-medium text-grey2 text-lg whitespace-nowrap">
              {t('NO_OPTION')}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
